import { URLS } from '@/lib/constants';
import { LIVE_SESSIONS_STALE_TIME, queryKeys } from '@/lib/queryClient';
import { fetchLiveSessionEvents } from '@/services/live-sessions.service';
import type {
  LiveSessionDisplayEvent,
  MembershipTier,
  WPLiveSessionEvent,
} from '@/types/live-session';
import { useQuery } from '@tanstack/react-query';
import { getLocales } from 'expo-localization';
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_HOUR = 60 * 60 * 1000;
const ABOUT_TO_START_MINUTES = 15;
/** Update every 30s so "in X min" countdown stays accurate (e.g. 13:08 → "in 7 min"). */
const TICK_MS = 30 * 1000;
/** While WP rejects the poll (403 from a firewall, 5xx), retry every 10 min instead of every 2. */
const LIVE_SESSIONS_ERROR_BACKOFF_MS = 10 * 60 * 1000;

/**
 * User's preferred locale for date/time (e.g. en-US → "Feb 3", "7:00 PM").
 * Web: navigator.languages[0] or navigator.language.
 * Native (iOS/Android): device language from expo-localization (getLocales()[0].languageTag).
 */
function getPreferredLocale(): string | undefined {
  if (Platform.OS !== 'web') {
    try {
      const locales = getLocales();
      const tag = locales?.[0]?.languageTag;
      if (tag) return tag;
    } catch {
      // fallback below
    }
  }
  if (typeof navigator !== 'undefined') {
    const nav = navigator as { languages?: readonly string[]; language?: string };
    const tag = nav.languages?.[0] ?? nav.language;
    if (tag) return tag;
  }
  return undefined;
}

function formatStartTimeUserTZ(timestampSeconds: number, locale?: string): string {
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) {
    return new Date(timestampSeconds * 1000).toLocaleString(locale);
  }
  const date = new Date(timestampSeconds * 1000);
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
  if (isToday) {
    return new Intl.DateTimeFormat(locale, opts).format(date);
  }
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...opts,
  }).format(date);
}

function formatDateOnly(timestampSeconds: number, locale?: string): string {
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) {
    return new Date(timestampSeconds * 1000).toLocaleDateString(locale);
  }
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(timestampSeconds * 1000));
}

function formatTimeOnly(timestampSeconds: number, locale?: string): string {
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) {
    return new Date(timestampSeconds * 1000).toLocaleTimeString(locale);
  }
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(timestampSeconds * 1000));
}

/** Event is live (for pill "Live" in list) only when session has actually started: now in [start, end]. */
function isEventActuallyLive(e: WPLiveSessionEvent, nowSec: number): boolean {
  const start = e.event_start_timestamp;
  const end = e.event_end_timestamp ?? start + 3600;
  return nowSec >= start && nowSec <= end;
}

/** Event starts within 15 min but not yet started. */
function isEventAboutToStart(e: WPLiveSessionEvent, nowSec: number): boolean {
  const start = e.event_start_timestamp;
  const aboutToStartEnd = nowSec + ABOUT_TO_START_MINUTES * 60;
  return nowSec < start && start <= aboutToStartEnd;
}

function toDisplayEvents(
  events: WPLiveSessionEvent[],
  nowSec: number,
  locale?: string
): LiveSessionDisplayEvent[] {
  const loc = locale ?? getPreferredLocale();
  return events.map((e) => {
    const live = isEventActuallyLive(e, nowSec);
    const aboutToStart = isEventAboutToStart(e, nowSec);
    const minutesUntilStart =
      aboutToStart && e.event_start_timestamp > nowSec
        ? Math.ceil((e.event_start_timestamp - nowSec) / 60)
        : null;
    return {
      ...e,
      displayStartTime: formatStartTimeUserTZ(e.event_start_timestamp, loc),
      displayDate: formatDateOnly(e.event_start_timestamp, loc),
      displayTime: formatTimeOnly(e.event_start_timestamp, loc),
      isLive: live,
      isAboutToStart: aboutToStart,
      minutesUntilStart,
    };
  });
}

function filterAndSlice(
  events: WPLiveSessionEvent[],
  tier: MembershipTier,
  nowMs: number
): WPLiveSessionEvent[] {
  const nowSec = Math.floor(nowMs / 1000);
  let windowEndMs = nowMs + 7 * MS_PER_DAY;
  let limit = 3;
  if (tier === 'pro') {
    windowEndMs = nowMs + MS_PER_HOUR * 24;
    limit = 8;
  }

  const windowEndSec = Math.floor(windowEndMs / 1000);
  const filtered = events
    .filter((e) => {
      if (e.is_canceled || e.is_past_event) return false;
      if (e.event_start_timestamp > windowEndSec) return false;
      // Pro-only sessions stay VISIBLE to regular members (badged "OS Pro"):
      // ~90% of the schedule is pro-only, so hiding them left members with a
      // near-empty list and no path to the in-app Pro upgrade (tapping a pro
      // session routes to the event page whose Join CTA opens the native
      // Apple upgrade for app-billed members).
      return true;
    })
    .sort((a, b) => a.event_start_timestamp - b.event_start_timestamp)
    .slice(0, limit);

  return filtered;
}

/** Whether any event is live (started) or about to start (within 15 min) – for header beacon. */
function hasLiveOrUpcoming(events: WPLiveSessionEvent[], nowSec: number): boolean {
  return events.some((e) => {
    if (e.is_canceled || e.is_past_event) return false;
    return isEventActuallyLive(e, nowSec) || isEventAboutToStart(e, nowSec);
  });
}

export interface UseLiveSessionsOptions {
  /** regular = 7 days / 3 events; Pro = 24h / 8 events; non_member = no fetch (simple link in UI). */
  membershipTier?: MembershipTier;
  /** If true, fetch on mount and when refetch is called. */
  enabled?: boolean;
}

export interface UseLiveSessionsResult {
  events: LiveSessionDisplayEvent[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  /** True when there is at least one event that is live or about to start (for beacon). */
  isLiveOrUpcoming: boolean;
  /** Full events calendar URL on WP. */
  fullScheduleUrl: string;
}

export function useLiveSessions(
  options: UseLiveSessionsOptions = {}
): UseLiveSessionsResult {
  const { membershipTier = 'regular', enabled = true } = options;
  const queryEnabled = enabled && membershipTier !== 'non_member';

  const {
    data: rawEvents = [],
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.liveSessions,
    queryFn: async (): Promise<WPLiveSessionEvent[]> => {
      const result = await fetchLiveSessionEvents();
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to load events');
      }
      return result.events;
    },
    staleTime: LIVE_SESSIONS_STALE_TIME,
    refetchInterval: (query) =>
      query.state.status === 'error' ? LIVE_SESSIONS_ERROR_BACKOFF_MS : LIVE_SESSIONS_STALE_TIME,
    enabled: queryEnabled,
  });

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const nowSec = Math.floor(nowMs / 1000);
  const filtered = filterAndSlice(rawEvents, membershipTier, nowMs);
  const events = toDisplayEvents(filtered, nowSec);
  const isLiveOrUpcoming = hasLiveOrUpcoming(filtered, nowSec);

  const fullScheduleUrl = URLS.EVENTS;

  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load events') : null;

  return {
    events,
    isLoading,
    error,
    refetch,
    isLiveOrUpcoming,
    fullScheduleUrl,
  };
}
